import type { FormEvent, ReactNode } from "react";
import { Link } from "react-router-dom";
import { formatKabulDisplay } from "./ScheduleFields";
import { FilterToolbar } from "./FilterToolbar";
import { LtrIsolate } from "./LtrIsolate";
import type { StatusTone } from "../lib/overview-ops";
import {
  KILL_SWITCH_LABEL,
  ctaAvailabilityDisplay,
  ctaInventoryCounts,
  ctaKindLabel,
  ctaStatusDisplay,
  killSwitchStatusDisplay,
  relatedSettingsLinks,
  type CtaDestinationRow,
  type CtaSortKey,
} from "../lib/settings-ops";
import { cmsStrings } from "../terminology";
import type { KillSwitchAdminState } from "../lib/api";

const TONE_BADGE: Record<StatusTone, string> = {
  success: "state-badge--published",
  warning: "state-badge--paused",
  danger: "state-badge--emergency",
  info: "state-badge--scheduled",
  neutral: "state-badge--archived",
};

type FilterBarProps = {
  filter: string;
  onFilterChange: (value: string) => void;
  query: string;
  onQueryChange: (value: string) => void;
  sort: CtaSortKey;
  onSortChange: (value: CtaSortKey) => void;
  counts: ReturnType<typeof ctaInventoryCounts>;
  updatedAt: string | null;
};

export function CtaFilterBar({
  filter,
  onFilterChange,
  query,
  onQueryChange,
  sort,
  onSortChange,
  counts,
  updatedAt,
}: FilterBarProps) {
  const chips: Array<{ id: string; label: string }> = [
    { id: "all", label: `${cmsStrings.inventory.filterAll} (${counts.total})` },
    { id: "approved", label: `${cmsStrings.settingsUi.filterApproved} (${counts.approved})` },
    { id: "builtin", label: `${cmsStrings.settingsUi.filterBuiltin} (${counts.builtin})` },
    { id: "pending", label: `${cmsStrings.settingsUi.filterPending} (${counts.pending})` },
    { id: "saved", label: cmsStrings.settingsUi.filterSaved },
  ];
  return (
    <FilterToolbar
      className="list-filter-bar announcements-filter-bar"
      aria-label={cmsStrings.settingsUi.filterCtaAria}
    >
      {updatedAt && (
        <p className="sites-inventory__muted" style={{ width: "100%" }}>
          {cmsStrings.settingsUi.lastServerSave}{" "}
          <LtrIsolate mono>{formatKabulDisplay(updatedAt)}</LtrIsolate>
        </p>
      )}
      <div
        className="filter-toolbar__chips announcements-filter-bar__chips"
        role="group"
        aria-label={cmsStrings.settingsUi.filterCtaAria}
      >
        {chips.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`btn btn--sm ${filter === c.id ? "btn--primary" : "btn--ghost"}`}
            onClick={() => onFilterChange(c.id)}
            aria-pressed={filter === c.id}
          >
            {c.label}
          </button>
        ))}
      </div>
      <label className="announcements-filter-bar__field">
        <span className="visually-hidden">{cmsStrings.settingsUi.searchUrlHidden}</span>
        <input
          type="search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder={cmsStrings.settingsUi.searchUrlPlaceholder}
          aria-label={cmsStrings.settingsUi.searchCtaAria}
          dir="ltr"
          className="ltr-isolate"
        />
      </label>
      <label className="announcements-filter-bar__field">
        <span>{cmsStrings.inventory.sortLabel}</span>
        <select
          value={sort}
          onChange={(e) => onSortChange(e.target.value as CtaSortKey)}
          aria-label={cmsStrings.settingsUi.sortCtaAria}
        >
          <option value="url">{cmsStrings.settingsUi.sortByUrl}</option>
          <option value="kind">{cmsStrings.settingsUi.sortByKind}</option>
          <option value="status">{cmsStrings.settingsUi.sortByStatus}</option>
        </select>
      </label>
    </FilterToolbar>
  );
}

export function CtaInspectSummary({
  item,
  counts,
  actions,
}: {
  item: CtaDestinationRow;
  counts: ReturnType<typeof ctaInventoryCounts>;
  actions?: ReactNode;
}) {
  const status = ctaStatusDisplay(item);
  const availability = ctaAvailabilityDisplay(item);
  const links = relatedSettingsLinks();

  return (
    <section className="content-inspect" aria-labelledby="cta-inspect-title">
      <header className="content-inspect__header">
        <div className="content-inspect__identity">
          <p className="content-inspect__kind">{cmsStrings.settingsUi.ctaListTitle}</p>
          <h2 id="cta-inspect-title" className="content-inspect__name">
            مقصد HTTPS
          </h2>
          <p className="content-inspect__id">
            <LtrIsolate mono>{item.url}</LtrIsolate>
          </p>
        </div>
        <div className="content-inspect__badges" aria-label={cmsStrings.settingsUi.statusDestinationAria}>
          <span className={`state-badge ${TONE_BADGE[status.tone]}`}>{status.label}</span>
          <span className={`state-badge ${TONE_BADGE[availability.tone]}`}>
            {availability.label}
          </span>
          <span className="state-badge state-badge--scheduled">{ctaKindLabel(item.kind)}</span>
        </div>
      </header>

      {status.detail && <p className="content-inspect__eligibility-detail">{status.detail}</p>}

      <dl className="content-inspect__facts">
        <div>
          <dt>URL</dt>
          <dd>
            <LtrIsolate mono>{item.url}</LtrIsolate>
          </dd>
        </div>
        <div>
          <dt>نوع</dt>
          <dd>{ctaKindLabel(item.kind)}</dd>
        </div>
        <div>
          <dt>{cmsStrings.settingsUi.listContext}</dt>
          <dd>
            تأییدشده <LtrIsolate mono>{counts.approved}</LtrIsolate>
            {" · پیش‌فرض "}
            <LtrIsolate mono>{counts.builtin}</LtrIsolate>
            {" · در انتظار "}
            <LtrIsolate mono>{counts.pending}</LtrIsolate>
          </dd>
        </div>
      </dl>

      <nav className="content-inspect__links" aria-label={cmsStrings.inventory.relatedContentAria}>
        {links.map((link) => (
          <Link key={link.id} to={link.to} className="btn btn--ghost btn--sm">
            {link.label}
          </Link>
        ))}
      </nav>

      {actions && <div className="content-inspect__actions">{actions}</div>}
    </section>
  );
}

export function CtaInventoryList({
  rows,
  selectedId,
  onSelect,
  renderActions,
}: {
  rows: CtaDestinationRow[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  renderActions: (row: CtaDestinationRow) => ReactNode;
}) {
  return (
    <>
      <div
        className="sites-inventory-table-wrap inventory-table-wrap"
        role="region"
        aria-label={cmsStrings.settingsUi.tableAria}
      >
        <table className="data-table content-inventory-table inventory-table">
          <thead>
            <tr>
              <th scope="col">URL</th>
              <th scope="col">نوع</th>
              <th scope="col">وضعیت</th>
              <th scope="col">دسترس‌پذیری</th>
              <th scope="col">
                <span className="visually-hidden">عملیات</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const selected = row.id === selectedId;
              const status = ctaStatusDisplay(row);
              const availability = ctaAvailabilityDisplay(row);
              return (
                <tr
                  key={row.id}
                  className={selected ? "sites-inventory-table__row--selected" : undefined}
                >
                  <td>
                    <button
                      type="button"
                      className="sites-inventory__select-btn"
                      onClick={() => onSelect(row.id)}
                      aria-pressed={selected}
                    >
                      <LtrIsolate mono>{row.url}</LtrIsolate>
                    </button>
                  </td>
                  <td>{ctaKindLabel(row.kind)}</td>
                  <td>
                    <span className={`state-badge ${TONE_BADGE[status.tone]}`}>{status.label}</span>
                  </td>
                  <td>
                    <span className={`state-badge ${TONE_BADGE[availability.tone]}`}>
                      {availability.label}
                    </span>
                  </td>
                  <td>
                    <div className="list-row__actions list-actions">
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => onSelect(row.id)}
                      >
                        جزئیات
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ul
        className="sites-inventory-cards content-inventory-cards inventory-cards"
        aria-label={cmsStrings.settingsUi.cardsAria}
      >
        {rows.map((row) => {
          const selected = row.id === selectedId;
          const status = ctaStatusDisplay(row);
          const availability = ctaAvailabilityDisplay(row);
          return (
            <li
              key={row.id}
              className={`sites-inventory-card${selected ? " sites-inventory-card--selected" : ""}`}
            >
              <button
                type="button"
                className="sites-inventory-card__hit"
                onClick={() => onSelect(row.id)}
                aria-pressed={selected}
              >
                <span className="sites-inventory-card__title">
                  <LtrIsolate mono>{row.url}</LtrIsolate>
                </span>
                <span className="sites-inventory-card__badges">
                  <span className={`state-badge ${TONE_BADGE[status.tone]}`}>{status.label}</span>
                  <span className={`state-badge ${TONE_BADGE[availability.tone]}`}>
                    {availability.label}
                  </span>
                  <span className="state-badge state-badge--scheduled">
                    {ctaKindLabel(row.kind)}
                  </span>
                </span>
              </button>
              <div className="sites-inventory-card__actions">{renderActions(row)}</div>
            </li>
          );
        })}
      </ul>
    </>
  );
}

export function KillSwitchInspectPanel({
  kill,
  loading,
  isPlatformOwner,
  killReason,
  onKillReasonChange,
  onEnableClick,
  onDisableClick,
}: {
  kill: KillSwitchAdminState | null;
  loading: boolean;
  isPlatformOwner: boolean;
  killReason: string;
  onKillReasonChange: (value: string) => void;
  onEnableClick: () => void;
  onDisableClick: () => void;
}) {
  if (loading || !kill) {
    return (
      <p className="sites-inventory__muted" role="status">
        {cmsStrings.killSwitch.loadingStatus}
      </p>
    );
  }

  const status = killSwitchStatusDisplay(kill.active);
  const links = relatedSettingsLinks().filter((l) =>
    ["announcements", "promotions", "sponsored", "observability"].includes(l.id),
  );
  const sc = cmsStrings.settingsComposer;

  return (
    <section className="content-inspect" aria-labelledby="kill-inspect-title">
      <header className="content-inspect__header">
        <div className="content-inspect__identity">
          <p className="content-inspect__kind">{sc.killKind}</p>
          <h2 id="kill-inspect-title" className="content-inspect__name">
            {KILL_SWITCH_LABEL}
          </h2>
        </div>
        <div className="content-inspect__badges" aria-label={cmsStrings.killSwitch.statusAria}>
          <span className={`state-badge ${TONE_BADGE[status.tone]}`}>{status.label}</span>
          <span className="state-badge state-badge--archived">{sc.platformWide}</span>
        </div>
      </header>

      {status.detail && <p className="content-inspect__eligibility-detail">{status.detail}</p>}

      <dl className="content-inspect__facts">
        {kill.reason && (
          <div>
            <dt>{sc.factReason}</dt>
            <dd>{kill.reason}</dd>
          </div>
        )}
        {kill.enabledAt && (
          <div>
            <dt>{sc.factFrom}</dt>
            <dd>
              <LtrIsolate mono>{formatKabulDisplay(kill.enabledAt)}</LtrIsolate>
            </dd>
          </div>
        )}
        {kill.expiresAt && (
          <div>
            <dt>{sc.factExpires}</dt>
            <dd>
              <LtrIsolate mono>{formatKabulDisplay(kill.expiresAt)}</LtrIsolate>
            </dd>
          </div>
        )}
        {kill.enabledByUserId && (
          <div>
            <dt>{sc.factActor}</dt>
            <dd>
              <LtrIsolate mono>{kill.enabledByUserId}</LtrIsolate>
            </dd>
          </div>
        )}
        <div>
          <dt>{sc.factCache}</dt>
          <dd>
            <LtrIsolate mono>
              max-age={kill.cacheMaxAgeSeconds}, must-revalidate
            </LtrIsolate>
          </dd>
        </div>
        <div>
          <dt>{sc.factMaxPropagation}</dt>
          <dd>
            <LtrIsolate mono>{kill.maxPropagationSeconds}s</LtrIsolate>
          </dd>
        </div>
      </dl>

      <nav className="content-inspect__links" aria-label={cmsStrings.killSwitch.affectedContentAria}>
        {links.map((link) => (
          <Link key={link.id} to={link.to} className="btn btn--ghost btn--sm">
            {link.label}
          </Link>
        ))}
      </nav>

      {!isPlatformOwner && <p className="field-hint">{sc.ownerOnlyHint}</p>}

      {isPlatformOwner && (
        <div className="content-inspect__actions form-actions">
          {!kill.active ? (
            <>
              <label>
                <span>{sc.reasonOptional}</span>
                <input
                  value={killReason}
                  onChange={(e) => onKillReasonChange(e.target.value.slice(0, 500))}
                  maxLength={500}
                  placeholder={sc.reasonPlaceholder}
                />
              </label>
              <button type="button" className="btn btn--accent" onClick={onEnableClick}>
                {sc.enableButton}
              </button>
            </>
          ) : (
            <button type="button" className="btn btn--primary" onClick={onDisableClick}>
              {sc.disableButton}
            </button>
          )}
        </div>
      )}
    </section>
  );
}

export function CtaAddForm({
  newUrl,
  onNewUrlChange,
  onSubmit,
}: {
  newUrl: string;
  onNewUrlChange: (value: string) => void;
  onSubmit: (e: FormEvent) => void;
}) {
  const sc = cmsStrings.settingsComposer;
  return (
    <form className="content-form content-form--in-card" onSubmit={onSubmit}>
      <p className="field-hint field-hint--block">{sc.addUrlHint}</p>
      <div className="form-row-inline">
        <label>
          <span>{sc.addUrlLabel}</span>
          <input
            type="url"
            dir="ltr"
            className="ltr-isolate"
            value={newUrl}
            onChange={(e) => onNewUrlChange(e.target.value)}
            placeholder="https://example.com/path"
          />
        </label>
        <button type="submit" className="btn btn--ghost btn--sm">
          {sc.add}
        </button>
      </div>
    </form>
  );
}
